import { campaignItemsToBuilderContext, type HelpMeCampaignItem } from './channelTypeMapping';

export type Channel = 'email' | 'googlePost' | 'push' | 'socialPost' | 'text';

export const CHANNEL_ORDER: readonly Channel[] = ['email', 'text', 'push', 'socialPost', 'googlePost'];

const nextUnfinishedChannel = (
  channels: readonly Channel[],
  finishedChannels: readonly Channel[],
  afterChannel?: Channel,
): Channel | undefined => {
  const startIndex = afterChannel == null ? -1 : CHANNEL_ORDER.indexOf(afterChannel);
  return CHANNEL_ORDER.find(
    (channel, index) => index > startIndex && channels.includes(channel) && !finishedChannels.includes(channel),
  );
};

const isGooglePostOnlySelection = (channels: readonly Channel[]): boolean => (
  channels.length === 1 && channels.includes('googlePost')
);

export type CreationMode = 'createOwn' | 'helpMe';

export type BuilderStep = 'builder' | 'channelSelection' | 'completed' | 'helpMeGenerating' | 'helpMeTopicSelection' | 'modeSelection' | null;

export type BuilderContext = {
  activeChannel?: Channel;
  campaignId?: number | null;
  creationMode?: CreationMode;
  emailChannelId?: number | null;
  followerSegmentId?: number | null;
  googlePostChannelId?: number | null;
  googlePostIsScheduled?: boolean;
  isIndividualFollowerSelection?: boolean;
  orderingOfferId?: number | null;
  remarketedAction?: string | null;
  remarketedCampaignItemId?: number | null;
  remarketedLocationId?: number | null;
  remarketedTagId?: number | null;
  pushChannelId?: number | null;
  restaurantId?: number;
  socialChannelId?: number | null;
  textChannelId?: number | null;
};

export type BuilderState = {
  channels: Channel[];
  context: BuilderContext;
  showEmailTemplateModal: boolean;
  showSaveAsDraftConfirmation: boolean;
  step: BuilderStep | null;
};

export type BuilderEvent =
  | { type: 'campaignBuilder.closeEmailTemplateModal' }
  | { type: 'campaignBuilder.closeSaveAsDraftConfirmation' }
  | { type: 'campaignBuilder.exit' }
  | { type: 'campaignBuilder.cancelHelpMeGeneration' }
  | { type: 'campaignBuilder.contentReady'; campaignItems: HelpMeCampaignItem[] }
  | { type: 'campaignBuilder.goToBuilder'; creationMode: CreationMode }
  | { type: 'campaignBuilder.goToNextChannel'; completedChannels: Channel[] }
  | { type: 'campaignBuilder.openChannel'; channel: Channel }
  | { type: 'campaignBuilder.openEmailTemplateModal' }
  | { type: 'campaignBuilder.openSaveAsDraftConfirmation' }
  | {
    type: 'campaignBuilder.openUnifiedBuilder';
    activeChannel: Channel;
    creationMode?: CreationMode;
    restaurantId: number;
    campaignId?: number | null;
    textChannelId?: number | null;
    emailChannelId?: number | null;
    socialChannelId?: number | null;
    googlePostChannelId?: number | null;
    pushChannelId?: number | null;
  }
  | {
    type: 'campaignBuilder.openChannelSelection';
    restaurantId: number;
    followerSegmentId?: number | null;
    isIndividualFollowerSelection?: boolean;
    orderingOfferId?: number | null;
    remarketedAction?: string | null;
    remarketedCampaignItemId?: number | null;
    remarketedLocationId?: number | null;
    remarketedTagId?: number | null;
  }
  | { type: 'campaignBuilder.openModeSelection'; activeChannel?: Channel; channels: Channel[] }
  | { type: 'campaignBuilder.openSuccessModal' }
  | { type: 'campaignBuilder.setCampaignId'; campaignId?: number }
  | { type: 'campaignBuilder.setEmailChannelId'; emailChannelId: number | null }
  | { type: 'campaignBuilder.setGooglePostChannelId'; googlePostChannelId: number | null; isScheduled: boolean }
  | { type: 'campaignBuilder.setSocialChannelId'; socialChannelId: number | null }
  | { type: 'campaignBuilder.setPushChannelId'; pushChannelId: number | null }
  | { type: 'campaignBuilder.setTextChannelId'; textChannelId: number | null }
  | { type: 'campaignBuilder.startHelpMeGeneration' };

export const builderInitialState: BuilderState = {
  channels: [],
  context: {},
  showEmailTemplateModal: false,
  showSaveAsDraftConfirmation: false,
  step: null,
};

const enterChannelBuilder = (prevState: BuilderState, channel: Channel, channels: Channel[]): BuilderState => ({
  ...prevState,
  channels,
  context: { ...prevState.context, activeChannel: channel },
  step: 'builder',
});

export const finishChannelEvent = (
  channels: readonly Channel[],
  activeChannel: Channel | undefined,
  completedChannels: readonly Channel[],
): BuilderEvent => {
  const hasNextChannel = nextUnfinishedChannel(channels, completedChannels, activeChannel);
  if (hasNextChannel) {
    return { completedChannels: [...completedChannels], type: 'campaignBuilder.goToNextChannel' };
  }
  return { type: 'campaignBuilder.openSuccessModal' };
};

export const campaignBuilderReducer = (
  prevState: BuilderState = builderInitialState,
  event: BuilderEvent,
): BuilderState => {
  switch (event.type) {
    case 'campaignBuilder.openEmailTemplateModal':
      return {
        ...prevState,
        showEmailTemplateModal: true,
      };
    case 'campaignBuilder.closeEmailTemplateModal':
      return {
        ...prevState,
        showEmailTemplateModal: false,
      };
    case 'campaignBuilder.openSaveAsDraftConfirmation':
      return {
        ...prevState,
        showSaveAsDraftConfirmation: true,
      };
    case 'campaignBuilder.closeSaveAsDraftConfirmation':
      return {
        ...prevState,
        showSaveAsDraftConfirmation: false,
      };
    case 'campaignBuilder.openChannelSelection':
      return {
        channels: [],
        context: {
          restaurantId: event.restaurantId,
          ...(event.followerSegmentId != null ? { followerSegmentId: event.followerSegmentId } : {}),
          ...(event.isIndividualFollowerSelection ? { isIndividualFollowerSelection: true } : {}),
          ...(event.orderingOfferId != null ? { orderingOfferId: event.orderingOfferId } : {}),
          ...(event.remarketedCampaignItemId != null ? {
            remarketedAction: event.remarketedAction ?? null,
            remarketedCampaignItemId: event.remarketedCampaignItemId,
            remarketedLocationId: event.remarketedLocationId ?? null,
            remarketedTagId: event.remarketedTagId ?? null,
          } : {}),
        },
        showEmailTemplateModal: false,
        showSaveAsDraftConfirmation: false,
        step: 'channelSelection',
      };
    case 'campaignBuilder.openModeSelection': {
      const googlePostOnly = isGooglePostOnlySelection(event.channels);
      const activeChannel = googlePostOnly ? 'googlePost' : (event.activeChannel ?? nextUnfinishedChannel(event.channels, []));
      return {
        ...prevState,
        channels: event.channels,
        context: {
          ...prevState.context,
          activeChannel,
          ...(googlePostOnly ? { creationMode: 'createOwn' } : {}),
        },
        step: googlePostOnly ? 'builder' : 'modeSelection',
      };
    }
    case 'campaignBuilder.openSuccessModal':
      return {
        ...prevState,
        step: 'completed',
      };
    case 'campaignBuilder.goToNextChannel': {
      const nextChannel = nextUnfinishedChannel(prevState.channels, event.completedChannels, prevState.context.activeChannel);
      if (!nextChannel) {
        return {
          ...prevState,
          step: 'completed',
        };
      }
      return enterChannelBuilder(prevState, nextChannel, prevState.channels);
    }
    case 'campaignBuilder.openChannel': {
      const channels = prevState.channels.includes(event.channel) ?
        prevState.channels :
        [...prevState.channels, event.channel];
      return enterChannelBuilder(prevState, event.channel, channels);
    }
    case 'campaignBuilder.goToBuilder':
      return {
        ...prevState,
        context: { ...prevState.context, creationMode: event.creationMode },
        step: event.creationMode === 'helpMe' ? 'helpMeTopicSelection' : 'builder',
      };
    case 'campaignBuilder.startHelpMeGeneration':
      return {
        ...prevState,
        step: 'helpMeGenerating',
      };
    case 'campaignBuilder.cancelHelpMeGeneration':
      return {
        ...prevState,
        step: 'helpMeTopicSelection',
      };
    case 'campaignBuilder.contentReady':
      if (prevState.step !== 'helpMeGenerating') {
        return prevState;
      }
      return {
        ...prevState,
        context: {
          ...prevState.context,
          ...campaignItemsToBuilderContext(event.campaignItems),
        },
        step: 'builder',
      };
    case 'campaignBuilder.openUnifiedBuilder':
      return {
        channels: [event.activeChannel],
        context: {
          activeChannel: event.activeChannel,
          creationMode: event.creationMode ?? 'createOwn',
          restaurantId: event.restaurantId,
          ...(event.campaignId !== undefined ? { campaignId: event.campaignId } : {}),
          ...(event.textChannelId !== undefined ? { textChannelId: event.textChannelId } : {}),
          ...(event.emailChannelId !== undefined ? { emailChannelId: event.emailChannelId } : {}),
          ...(event.socialChannelId !== undefined ? { socialChannelId: event.socialChannelId } : {}),
          ...(event.googlePostChannelId !== undefined ? { googlePostChannelId: event.googlePostChannelId } : {}),
          ...(event.pushChannelId !== undefined ? { pushChannelId: event.pushChannelId } : {}),
        },
        showEmailTemplateModal: false,
        showSaveAsDraftConfirmation: false,
        step: 'builder',
      };
    case 'campaignBuilder.setCampaignId':
      return {
        ...prevState,
        context: { ...prevState.context, campaignId: event.campaignId },
      };
    case 'campaignBuilder.setEmailChannelId':
      return {
        ...prevState,
        context: { ...prevState.context, emailChannelId: event.emailChannelId },
      };
    case 'campaignBuilder.setGooglePostChannelId':
      return {
        ...prevState,
        context: {
          ...prevState.context,
          googlePostChannelId: event.googlePostChannelId,
          googlePostIsScheduled: event.isScheduled,
        },
      };
    case 'campaignBuilder.setSocialChannelId':
      return {
        ...prevState,
        context: { ...prevState.context, socialChannelId: event.socialChannelId },
      };
    case 'campaignBuilder.setPushChannelId':
      return {
        ...prevState,
        context: {
          ...prevState.context,
          pushChannelId: event.pushChannelId,
        },
      };
    case 'campaignBuilder.setTextChannelId':
      return {
        ...prevState,
        context: {
          ...prevState.context,
          textChannelId: event.textChannelId,
        },
      };
    case 'campaignBuilder.exit':
      return builderInitialState;
    default:
      return prevState;
  }
};
